import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import { useSEO } from '@/hooks/useSEO';
import { allLeagues, allMatches, allNews, allPlayers, allTeams } from '@/utils/data';

export default function AboutPage() {
  useSEO({
    title: '关于我们 - 篮球比分',
    description: '篮球比分网站定位、联系方式与使用说明，了解本站如何为篮球爱好者提供比分与数据查询服务。',
    keywords: '关于篮球比分,网站介绍,联系方式,使用说明',
  });

  const stats = [
    { label: '收录联赛', value: allLeagues.length, icon: 'ri-trophy-line' },
    { label: '收录球队', value: allTeams.length, icon: 'ri-team-line' },
    { label: '收录球员', value: allPlayers.length, icon: 'ri-user-star-line' },
    { label: '赛事场次', value: allMatches.length, icon: 'ri-basketball-line' },
    { label: '新闻资讯', value: allNews.length, icon: 'ri-newspaper-line' },
  ];

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <header className="mt-4 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">关于篮球比分</h1>
        <p className="mt-2 text-sm md:text-[15px] text-foreground-600 max-w-3xl">
          一个聚焦篮球赛事比分与数据的体育资讯平台。
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg bg-background-50 border border-background-200/80 px-4 py-4">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-2">
              <i className={s.icon}></i>
            </span>
            <span className="block font-heading font-extrabold text-2xl text-foreground-950">{s.value}</span>
            <span className="block text-xs text-foreground-500 mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="网站简介" icon="ri-information-line">
            <div className="px-4 md:px-5 py-5 space-y-4 text-sm md:text-[15px] text-foreground-700 leading-7">
              <p>
                篮球比分面向广大篮球爱好者，致力于提供全面、及时的篮球比赛比分与数据查询服务。网站内容涵盖国内外多项篮球赛事与联赛，
                包括 NBA、CBA、欧洲篮球联赛、WNBA 等，帮助用户快速获取最新赛况、历史战绩以及球队和球员信息。
              </p>
              <p>
                我们围绕比赛本身组织内容：首页聚焦当前热点赛事与实时比分，分类页提供按联赛、赛季、状态与球队的多维筛选，
                比赛详情页呈现完整的对阵信息、技术统计与关键事件，球队与球员页面则帮助用户深入了解赛季表现。
              </p>
              <p>
                目标用户包括篮球球迷、数据分析师、体育记者及相关从业者。我们希望以简洁高效的浏览体验、权威及时的数据呈现，
                满足不同场景下的信息获取需求。
              </p>
            </div>
          </SectionCard>

          <SectionCard title="使用说明" icon="ri-question-line">
            <ul className="px-4 md:px-5 py-5 space-y-3.5 text-sm md:text-[15px] text-foreground-700">
              <li className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-search-line"></i></span>
                <span><strong className="text-foreground-900">搜索：</strong>在顶部搜索框输入赛事、球队或球员关键词，即可快速定位相关内容。</span>
              </li>
              <li className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-filter-3-line"></i></span>
                <span><strong className="text-foreground-900">筛选：</strong>在赛事分类页通过赛季年份、比赛状态与球队名称组合筛选，并按时间或热度排序。</span>
              </li>
              <li className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-basketball-line"></i></span>
                <span><strong className="text-foreground-900">查看比分：</strong>点击任意比赛卡片进入比赛详情页，查看各节比分、技术统计与比赛事件。</span>
              </li>
              <li className="flex gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-primary-500 shrink-0 mt-0.5"><i className="ri-team-line"></i></span>
                <span><strong className="text-foreground-900">球队与球员：</strong>通过球队或球员页面了解赛季排名、近期战绩与个人数据。</span>
              </li>
            </ul>
          </SectionCard>

          <SectionCard title="免责声明" icon="ri-shield-check-line">
            <p className="px-4 md:px-5 py-5 text-sm text-foreground-600 leading-7">
              本站当前展示的比分、排名与统计数据均为演示示例，未接入真实赛事数据接口，仅用于产品功能展示。
              未来接入权威数据源后，我们将持续更新内容的准确性与时效性。
            </p>
          </SectionCard>
        </div>

        <aside className="space-y-6">
          <SectionCard title="联系方式" icon="ri-contacts-line">
            <ul className="px-4 md:px-5 py-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0"><i className="ri-mail-send-line"></i></span>
                <div>
                  <span className="block text-xs text-foreground-500">合作与投稿邮箱</span>
                  <span className="text-foreground-900">contact@basketballscores.demo</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0"><i className="ri-customer-service-2-line"></i></span>
                <div>
                  <span className="block text-xs text-foreground-500">客服时间</span>
                  <span className="text-foreground-900">工作日 09:00 - 18:00</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0"><i className="ri-map-pin-line"></i></span>
                <div>
                  <span className="block text-xs text-foreground-500">办公地点</span>
                  <span className="text-foreground-900">中国 · 上海</span>
                </div>
              </li>
            </ul>
            <div className="px-4 md:px-5 pb-5">
              <span className="block text-xs text-foreground-500 mb-2">关注我们</span>
              <div className="flex items-center gap-2">
                {['ri-wechat-line', 'ri-weibo-line', 'ri-youtube-line', 'ri-mail-line'].map((icon) => (
                  <span key={icon} className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer">
                    <i className={icon}></i>
                  </span>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="快速导航" icon="ri-links-line">
            <div className="px-4 md:px-5 py-5 grid grid-cols-2 gap-2">
              {[
                { label: '全部赛事', to: '/category/all' },
                { label: '球队资料', to: '/teams' },
                { label: '球员数据', to: '/players' },
                { label: '新闻资讯', to: '/news' },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-2.5 rounded-md bg-background-100 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer text-center"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}